import { Box, LinearProgress, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { auth } from "../auth/auth";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import FormTextField, { emailValidationPattern } from "../components/formComponents/FormTextField";

function getEmailAddress() {
  const email = localStorage.getItem("emailForSignIn");
  if (email) {
    return email;
  }
  return window.prompt("Podaj swój adres email");
}

interface EmailLoginFormState {
  email: string;
}

interface Props {
  readonly isSignInWithEmailLink: boolean;
  readonly onStartLogin: (email: string) => Promise<void>;
  readonly onLoginSuccess: () => void;
  readonly onLoginFailed: (error: unknown) => void;
}

const EmailLinkLogin: FC<Props> = ({ onStartLogin, isSignInWithEmailLink, onLoginSuccess, onLoginFailed }) => {
  const { control, handleSubmit } = useForm<EmailLoginFormState>({
    defaultValues: { email: "" },
  });
  const [showLinkSent, setShowLinkSent] = useState(false);

  useEffect(() => {
    if (isSignInWithEmailLink) {
      const email = getEmailAddress();
      if (!email) {
        onLoginFailed(new Error("Brak adresu email"));
        return;
      }
      auth
        .signInWithEmailLink(email, window.location.href)
        .then(() => {
          localStorage.removeItem("emailForSignIn");
          onLoginSuccess();
        })
        .catch((error) => {
          onLoginFailed(error);
        });
    }
  }, [isSignInWithEmailLink]);

  if (isSignInWithEmailLink) {
    return (
      <>
        <Typography>Sprawdzanie danych do logowania...</Typography>
        <LinearProgress />
      </>
    );
  }

  const handleStartLogin = async (email: string) => {
    await onStartLogin(email);
    localStorage.setItem("emailForSignIn", email);
    setShowLinkSent(true);
  };

  if (showLinkSent) {
    return (
      <>
        <Typography>Link pozwalający zalogować się do aplikacji został wysłany na wskazany adres email.</Typography>
        <Typography>Sprawdź swoją skrzynkę odbiorczą.</Typography>
      </>
    );
  }

  return (
    <>
      <Typography
        variant="h4"
        gutterBottom
        color="text.primary"
        sx={{
          fontWeight: "bold",
        }}
      >
        Logowanie bez hasła
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
        Podaj adres e-mail, a wyślemy Ci bezpieczny, jednorazowy link do zalogowania.
      </Typography>

      <FormTextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Adres e-mail"
        name="email"
        autoComplete="email"
        autoFocus
        type="email"
        variant="outlined"
        control={control}
        rules={{ pattern: emailValidationPattern }}
        InputProps={{ sx: { borderRadius: 16 } }}
      />
      <Box sx={{ mt: 1, mb: 1 }} />
      <AsyncLoadingButton
        onClick={handleSubmit((s) => handleStartLogin(s.email))}
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        sx={{ py: 1.5, mb: 2, fontSize: "1.1rem", borderRadius: 16, boxShadow: "0 4px 12px rgba(53, 87, 161, 0.3)" }}
      >
        Wyślij link logowania
      </AsyncLoadingButton>
    </>
  );
};

export default EmailLinkLogin;
